import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Users, Home, ArrowRightLeft, LogOut } from 'lucide-react';

const PLACEMENT_TYPES = ['PERMANENT', 'TEMPORARY', 'RESPITE', 'EMERGENCY', 'TRANSITIONAL'];
const STATUS_COLORS = { ACTIVE: 'default', SCHEDULED: 'secondary', COMPLETED: 'outline', CANCELLED: 'outline', ON_LEAVE: 'secondary', HOSPITALISED: 'destructive' };

export default function PlacementsPage() {
  const { isAdmin } = useAuth();
  const [placements, setPlacements] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('ACTIVE');
  const [houses, setHouses] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showTransfer, setShowTransfer] = useState(null);
  const [showDischarge, setShowDischarge] = useState(null);

  const [form, setForm] = useState({ participant_id: '', property_id: '', room_id: '', placement_type: 'PERMANENT', start_date: '', funding_type: '' });
  const [transferForm, setTransferForm] = useState({ new_property_id: '', new_room_id: '', transfer_date: '', reason: '' });
  const [dischargeForm, setDischargeForm] = useState({ discharge_date: '', reason: '' });
  const [transferRooms, setTransferRooms] = useState([]);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, pageSize: '20' });
      if (statusFilter && statusFilter !== 'ALL') params.set('status', statusFilter);
      const { data } = await api.get(`/accommodation/placements?${params}`);
      setPlacements(data.items || []); setTotal(data.total || 0);
    } catch { toast.error('Failed to load placements'); }
  }, [page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    Promise.allSettled([
      api.get('/houses?pageSize=100'),
      api.get('/participants?pageSize=100'),
    ]).then(([hR, pR]) => {
      if (hR.status === 'fulfilled') setHouses(hR.value.data.items || hR.value.data.data || []);
      if (pR.status === 'fulfilled') setParticipants(pR.value.data.items || pR.value.data.data || []);
    });
  }, []);

  const loadRooms = async (houseId, setter) => {
    try { const { data } = await api.get(`/houses/${houseId}/rooms`); setter(data.data || []); }
    catch { setter([]); }
  };

  const handleCreate = async () => {
    if (!form.participant_id || !form.property_id || !form.room_id || !form.start_date) {
      toast.error('Fill all required fields'); return;
    }
    try {
      await api.post('/accommodation/placements', form);
      toast.success('Placement created');
      setShowCreate(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleTransfer = async () => {
    if (!transferForm.new_property_id || !transferForm.new_room_id || !transferForm.transfer_date) {
      toast.error('Fill all required fields'); return;
    }
    try {
      await api.post('/accommodation/placements/transfer', { allocation_id: showTransfer.id, ...transferForm });
      toast.success('Placement transferred');
      setShowTransfer(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDischarge = async () => {
    try {
      await api.post('/accommodation/placements/discharge', { allocation_id: showDischarge.id, ...dischargeForm });
      toast.success('Participant discharged');
      setShowDischarge(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="placements-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Users className="w-6 h-6" />Participant Placements</h1>
          <p className="text-muted-foreground mt-1">{total} placements</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowCreate(true)} data-testid="create-placement-btn"><Plus className="w-4 h-4 mr-2" />New Placement</Button>
        )}
      </div>

      <div className="flex items-center gap-3">
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All</SelectItem>
            <SelectItem value="ACTIVE">Active</SelectItem>
            <SelectItem value="SCHEDULED">Scheduled</SelectItem>
            <SelectItem value="COMPLETED">Completed</SelectItem>
            <SelectItem value="ON_LEAVE">On Leave</SelectItem>
            <SelectItem value="HOSPITALISED">Hospitalised</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {placements.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">No placements found</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {placements.map(p => (
            <Card key={p.id} data-testid={`placement-${p.id}`}>
              <CardContent className="py-3 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Users className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{p.participant_name || p.participant_id}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Home className="w-3 h-3" /><span>{p.property_name || p.house_id}</span>
                      {p.room_name && <span>· {p.room_name}</span>}
                      <Badge variant="outline" className="text-[10px]">{p.placement_type || 'PERMANENT'}</Badge>
                      <span>From: {p.move_in_date || p.start_date || '-'}</span>
                      {p.funding_type && <span>· {p.funding_type}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={STATUS_COLORS[p.status] || 'secondary'}>{p.status}</Badge>
                  {isAdmin && p.status === 'ACTIVE' && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => { setShowTransfer(p); setTransferForm({ new_property_id: '', new_room_id: '', transfer_date: '', reason: '' }); }} data-testid={`transfer-${p.id}`}>
                        <ArrowRightLeft className="w-3 h-3 mr-1" />Transfer
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => { setShowDischarge(p); setDischargeForm({ discharge_date: '', reason: '' }); }} data-testid={`discharge-${p.id}`}>
                        <LogOut className="w-3 h-3 mr-1" />Discharge
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Placement Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New Placement</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Participant *</Label>
              <Select value={form.participant_id} onValueChange={v => setForm(p => ({ ...p, participant_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Select participant" /></SelectTrigger>
                <SelectContent>{participants.map(p => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Property *</Label>
              <Select value={form.property_id} onValueChange={v => { setForm(p => ({ ...p, property_id: v, room_id: '' })); loadRooms(v, setRooms); }}>
                <SelectTrigger><SelectValue placeholder="Select property" /></SelectTrigger>
                <SelectContent>{houses.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Room *</Label>
              <Select value={form.room_id} onValueChange={v => setForm(p => ({ ...p, room_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Select room" /></SelectTrigger>
                <SelectContent>{rooms.map(r => <SelectItem key={r.id} value={r.id}>{r.name || r.room_no}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Type</Label>
                <Select value={form.placement_type} onValueChange={v => setForm(p => ({ ...p, placement_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PLACEMENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Start Date *</Label><Input type="date" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} /></div>
            </div>
            <div><Label>Funding Type</Label>
              <Select value={form.funding_type} onValueChange={v => setForm(p => ({ ...p, funding_type: v }))}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="SELF_MANAGED">Self Managed</SelectItem>
                  <SelectItem value="NDIA_MANAGED">NDIA Managed</SelectItem>
                  <SelectItem value="PLAN_MANAGED">Plan Managed</SelectItem>
                  <SelectItem value="SIL_MANAGED">SIL Managed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="confirm-placement">Create Placement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={!!showTransfer} onOpenChange={() => setShowTransfer(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Transfer Placement</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Transferring: {showTransfer?.participant_name}</p>
          <div className="space-y-4">
            <div><Label>New Property *</Label>
              <Select value={transferForm.new_property_id} onValueChange={v => { setTransferForm(p => ({ ...p, new_property_id: v, new_room_id: '' })); loadRooms(v, setTransferRooms); }}>
                <SelectTrigger><SelectValue placeholder="Select property" /></SelectTrigger>
                <SelectContent>{houses.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>New Room *</Label>
              <Select value={transferForm.new_room_id} onValueChange={v => setTransferForm(p => ({ ...p, new_room_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Select room" /></SelectTrigger>
                <SelectContent>{transferRooms.map(r => <SelectItem key={r.id} value={r.id}>{r.name || r.room_no}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Transfer Date *</Label><Input type="date" value={transferForm.transfer_date} onChange={e => setTransferForm(p => ({ ...p, transfer_date: e.target.value }))} /></div>
            <div><Label>Reason</Label><Input value={transferForm.reason} onChange={e => setTransferForm(p => ({ ...p, reason: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransfer(null)}>Cancel</Button>
            <Button onClick={handleTransfer} data-testid="confirm-transfer">Transfer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discharge Dialog */}
      <Dialog open={!!showDischarge} onOpenChange={() => setShowDischarge(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Discharge Participant</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Discharging: {showDischarge?.participant_name}</p>
          <div className="space-y-4">
            <div><Label>Discharge Date</Label><Input type="date" value={dischargeForm.discharge_date} onChange={e => setDischargeForm(p => ({ ...p, discharge_date: e.target.value }))} /></div>
            <div><Label>Reason</Label><Input value={dischargeForm.reason} onChange={e => setDischargeForm(p => ({ ...p, reason: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDischarge(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDischarge} data-testid="confirm-discharge">Discharge</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
